import React from 'react';
import AdminModal from '../admin/admin-modal.jsx';
import AgentHelpModal from '../agent-help/agent-help-modal.jsx';
import styles from './license-gate.css';
import parambhLogo from '../menu-bar/logo.png';
import supabase from '../../lib/supabase-client.js';

const apiUrl = process.env.AIPNT_API_URL;
const agentUrl = process.env.AIPNT_DEVICE_AGENT_URL;

class LicenseGate extends React.Component {
    constructor(props) {
        super(props);
        const isAlreadyLicensed = typeof window !== 'undefined' && window.aipnt_licensed;
        this.state = {
            state: isAlreadyLicensed ? 'licensed' : 'checking',
            message: '',
            email: '',
            password: '',
            activationCode: '',
            activeTab: 'login',
            adminDashboardVisible: false,
            helpModalVisible: false
        };
        this.verify = this.verify.bind(this);
        this.activate = this.activate.bind(this);
    }
    componentDidMount() {
        if (this.state.state !== 'licensed') {
            this.verify();
        }
    }
    async verify() {
        try {
            const identityResponse = await fetch(`${agentUrl}/v1/identity`);
            const identity = await identityResponse.json();
            const challengeResponse = await fetch(`${apiUrl}/api/licensing/challenge`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ deviceId: identity.deviceId })
            });
            if (!challengeResponse.ok) throw new Error('This computer has not been activated.');
            const challenge = await challengeResponse.json();
            const proofResponse = await fetch(`${agentUrl}/v1/sign`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ nonce: challenge.nonce })
            });
            const proof = await proofResponse.json();
            const sessionResponse = await fetch(`${apiUrl}/api/licensing/session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ challengeId: challenge.id, signature: proof.signature })
            });
            if (!sessionResponse.ok) throw new Error('Device licence verification failed.');
            const session = await sessionResponse.json();
            sessionStorage.setItem('aipnt_device_session', session.accessToken);
            if (localStorage.getItem('aipnt_explicit_logout') === 'true') {
                this.setState({ state: 'blocked', message: 'You have been signed out.', isDeviceActivated: true });
            } else {
                // Device is activated — now verify the user has an active Supabase session
                let hasUserSession = false;
                if (supabase) {
                    try {
                        const { data } = await supabase.auth.getSession();
                        hasUserSession = !!(data && data.session);
                    } catch (_ignored) {
                        hasUserSession = false;
                    }
                } else {
                    // No Supabase configured — skip user-level auth
                    hasUserSession = true;
                }

                if (hasUserSession) {
                    if (typeof window !== 'undefined') {
                        window.aipnt_licensed = true;
                    }
                    this.setState({ state: 'licensed' });
                } else {
                    // Device is activated but no user is logged in — show login form
                    this.setState({
                        state: 'blocked',
                        message: 'Please sign in with your email and password.',
                        isDeviceActivated: true
                    });
                }
            }
        } catch (error) {
            this.setState({
                state: 'blocked',
                message: localStorage.getItem('aipnt_explicit_logout') === 'true' ?
                    'You have been signed out.' :
                    error.message,
                isDeviceActivated: false
            });
        }
    }
    async activate(event) {
        event.preventDefault();
        try {
            if (!supabase) throw new Error('Supabase configuration is missing.');
            const signIn = await supabase.auth.signInWithPassword({
                email: this.state.email,
                password: this.state.password
            });
            if (signIn.error) throw signIn.error;

            if (this.state.isDeviceActivated) {
                // Device already activated — get a device session and bind user to this device
                localStorage.removeItem('aipnt_explicit_logout');

                // Get device session first
                const identityRes = await fetch(`${agentUrl}/v1/identity`);
                const identity = await identityRes.json();
                const challengeRes = await fetch(`${apiUrl}/api/licensing/challenge`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ deviceId: identity.deviceId })
                });
                if (!challengeRes.ok) throw new Error('Device verification failed.');
                const challenge = await challengeRes.json();
                const proofRes = await fetch(`${agentUrl}/v1/sign`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ nonce: challenge.nonce })
                });
                const proof = await proofRes.json();
                const sessionRes = await fetch(`${apiUrl}/api/licensing/session`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ challengeId: challenge.id, signature: proof.signature })
                });
                if (!sessionRes.ok) throw new Error('Device session failed.');
                const session = await sessionRes.json();
                sessionStorage.setItem('aipnt_device_session', session.accessToken);

                // Now bind user to this device
                const bindRes = await fetch(`${apiUrl}/api/licensing/bind-user`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.accessToken}`
                    },
                    body: JSON.stringify({ supabaseToken: signIn.data.session.access_token })
                });
                if (!bindRes.ok) {
                    const bindErr = await bindRes.json();
                    // Sign out from Supabase since binding failed
                    await supabase.auth.signOut();
                    throw new Error(bindErr.error || 'This account cannot be used on this system.');
                }

                this.setState({ state: 'checking', message: '' });
                this.verify();
                return;
            }

            const identity = await fetch(`${agentUrl}/v1/identity`).then(response => response.json());
            const response = await fetch(`${apiUrl}/api/licensing/activate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${signIn.data.session.access_token}`
                },
                body: JSON.stringify({
                    activationCode: this.state.activationCode,
                    deviceId: identity.deviceId,
                    publicKey: identity.publicKey,
                    deviceName: identity.deviceName
                })
            });
            if (!response.ok) throw new Error((await response.json()).error || 'Activation failed.');
            localStorage.removeItem('aipnt_explicit_logout');
            this.setState({ state: 'checking', message: '' });
            this.verify();
        } catch (error) {
            this.setState({ message: error.message });
        }
    }
    render() {
        if (this.state.state === 'licensed') return this.props.children;

        if (this.state.state === 'checking') {
            return (
                <div className={styles.checkingContainer}>
                    <img
                        className={styles.checkingLogo}
                        src={parambhLogo}
                        alt="Parambh"
                    />
                    <div className={styles.spinner} />
                    <div className={styles.checkingText}>{'Verifying system licence…'}</div>
                </div>
            );
        }

        return (
            <main className={styles.container}>
                {/* Left brand panel (visible on wide screens) */}
                <div className={styles.brandPanel}>
                    <div className={styles.brandPanelInner}>
                        <img
                            className={styles.brandLogo}
                            src={parambhLogo}
                            alt="Parambh Coding Platform"
                        />
                        <p className={styles.brandTagline}>
                            {'An AI-powered coding platform built for the next generation of creators.'}
                        </p>
                        <div className={styles.brandFeatures}>
                            <div className={styles.brandFeature}>
                                <div
                                    className={styles.brandFeatureDot}
                                    style={{ background: '#855CD6', color: 'rgba(133,92,214,0.75)' }}
                                />
                                <span>{'Visual block-based programming'}</span>
                            </div>
                            <div className={styles.brandFeature}>
                                <div
                                    className={styles.brandFeatureDot}
                                    style={{ background: '#4C97FF', color: 'rgba(76,151,255,0.75)' }}
                                />
                                <span>{'AI-assisted learning with Ask ATAT'}</span>
                            </div>
                            <div className={styles.brandFeature}>
                                <div
                                    className={styles.brandFeatureDot}
                                    style={{ background: '#0FBD8C', color: 'rgba(15,189,140,0.75)' }}
                                />
                                <span>{'Hardware & IoT integration'}</span>
                            </div>
                            <div className={styles.brandFeature}>
                                <div
                                    className={styles.brandFeatureDot}
                                    style={{ background: '#CF63CF', color: 'rgba(207,99,207,0.75)' }}
                                />
                                <span>{'Machine learning tools built in'}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right login panel */}
                <div className={styles.loginPanel}>
                    <div className={styles.card}>
                        <div className={styles.header}>
                            <div className={styles.headerLogos}>
                                <img
                                    className={styles.headerLogo}
                                    src={parambhLogo}
                                    alt="Parambh"
                                />
                            </div>
                            <h2 className={styles.title}>{'Welcome back'}</h2>
                            <p className={styles.subtitle}>{'Sign in to access your workspace'}</p>
                        </div>

                        <div className={styles.tabs}>
                            <div
                                className={`${styles.tab} ${this.state.activeTab === 'login' ? styles.activeTab : ''}`}
                                onClick={() => this.setState({ activeTab: 'login' })}
                            >
                                {'Log In / Activate'}
                            </div>
                            <div
                                className={`${styles.tab} ${this.state.activeTab === 'signup' ? styles.activeTab : ''}`}
                                onClick={() => this.setState({ activeTab: 'signup' })}
                            >
                                {'Sign Up'}
                            </div>
                        </div>

                        {this.state.message && (
                            <div className={styles.errorMessage}>
                                {this.state.message}
                            </div>
                        )}

                        {this.state.activeTab === 'login' ? (
                            <form
                                className={styles.form}
                                onSubmit={this.activate}
                            >
                                <div className={styles.inputGroup}>
                                    <label className={styles.label}>{'Email Address'}</label>
                                    <input
                                        className={styles.input}
                                        required
                                        type="email"
                                        placeholder="you@example.com"
                                        value={this.state.email}
                                        onChange={event => this.setState({ email: event.target.value })}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label className={styles.label}>{'Password'}</label>
                                    <input
                                        className={styles.input}
                                        required
                                        type="password"
                                        placeholder="Enter your password"
                                        value={this.state.password}
                                        onChange={event => this.setState({ password: event.target.value })}
                                    />
                                </div>
                                {!this.state.isDeviceActivated && (
                                    <div className={styles.inputGroup}>
                                        <label className={styles.label}>{'Activation Code'}</label>
                                        <input
                                            className={styles.input}
                                            required
                                            type="text"
                                            placeholder="Enter device activation code"
                                            value={this.state.activationCode}
                                            onChange={event => this.setState({ activationCode: event.target.value })}
                                        />
                                    </div>
                                )}
                                <button
                                    className={styles.submitButton}
                                    type="submit"
                                >
                                    {this.state.isDeviceActivated ? 'Log In' : 'Activate & Log In'}
                                </button>
                                <span
                                    className={styles.formHelpTip}
                                    onClick={() => this.setState({ helpModalVisible: true })}
                                >
                                    {'Need help installing agents? Open Setup Guide →'}
                                </span>
                            </form>
                        ) : (
                            <div className={styles.signupMessage}>
                                <p>
                                    {'To create a new account, please contact your '}
                                    <strong>{'system administrator'}</strong>
                                    {' for an activation code and admin privileges.'}
                                </p>
                                <button
                                    className={styles.submitButton}
                                    style={{ width: '100%', marginTop: '1.25rem' }}
                                    onClick={() => this.setState({ activeTab: 'login' })}
                                >
                                    {'Back to Log In'}
                                </button>
                            </div>
                        )}

                        <div className={styles.adminLink}>
                            <button
                                type="button"
                                className={styles.helpLinkButton}
                                onClick={() => this.setState({ helpModalVisible: true })}
                            >
                                {'🛠️ Agent Setup Guide'}
                            </button>
                            <button
                                type="button"
                                className={styles.adminLinkButton}
                                onClick={() => this.setState({ adminDashboardVisible: true })}
                            >
                                {'⚙ Admin Dashboard'}
                            </button>
                        </div>
                    </div>
                </div>

                {this.state.adminDashboardVisible && (
                    <AdminModal onRequestClose={() => this.setState({ adminDashboardVisible: false })} />
                )}

                {this.state.helpModalVisible && (
                    <AgentHelpModal onRequestClose={() => this.setState({ helpModalVisible: false })} />
                )}
            </main>
        );
    }
}

export { supabase };
export default LicenseGate;
